import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  Loader2, Clock, CheckCircle2, FolderOpen, AlertTriangle, Timer,
  Gauge, Users, RotateCcw, TrendingUp, Zap, Archive, MessageSquareReply,
  FileSpreadsheet, BarChart3, Send, Mail, X, PauseCircle, PlayCircle, CheckCheck, Layers,
  CalendarClock, History, ArrowUp, ArrowDown, ChevronsUpDown, ThumbsDown, ShieldCheck,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  Tooltip, Legend, AreaChart, Area, CartesianGrid,
} from "recharts";

const NAVY = "#0C314C";
const LIME = "#45E69A";

const fmtHoras = (h) => {
  if (h == null) return "—";
  if (h <= 0) return "0 h";
  if (h >= 48) return `${(h / 24).toFixed(1)} días`;
  if (h >= 1) return `${h.toFixed(1)} h`;
  return `${Math.round(h * 60)} min`;
};

const ESTADO_META = {
  abierto: { label: "Abierto", color: "#3b82f6", accent: "bg-blue-500", icon: PlayCircle },
  en_progreso: { label: "En Progreso", color: "#eab308", accent: "bg-yellow-500", icon: Clock },
  pendiente: { label: "Pendiente", color: "#f97316", accent: "bg-orange-500", icon: PauseCircle },
  resuelto: { label: "Resuelto", color: "#22c55e", accent: "bg-green-500", icon: CheckCircle2 },
  cerrado: { label: "Cerrado", color: NAVY, accent: "bg-[#0C314C]", icon: CheckCheck },
  completado: { label: "Completado", color: LIME, accent: "bg-lime-500", icon: CheckCheck },
};
const PRIO_COLOR = {
  "crítica": "#ef4444", critica: "#ef4444", alta: "#f97316",
  media: "#3b82f6", baja: "#94a3b8", urgente: "#dc2626",
};
const DEPT_PALETTE = [NAVY, "#2a4d7a", LIME, "#14b8a6", "#f59e0b", "#0ea5e9", "#8b5cf6", "#ec4899"];

const cap = (s) => (s || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const RecipientPicker = ({ catalog, selected, onChange, testidPrefix }) => {
  const [q, setQ] = useState("");
  const filtered = (catalog || []).filter((u) => {
    const t = `${u.nombre} ${u.email}`.toLowerCase();
    return t.includes(q.toLowerCase());
  });
  const toggle = (email) => {
    if (selected.includes(email)) onChange(selected.filter((e) => e !== email));
    else onChange([...selected, email]);
  };
  return (
    <div className="space-y-2">
      <Input
        placeholder="Buscar usuario por nombre o correo..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        data-testid={`${testidPrefix}-search`}
      />
      <div className="border rounded-md max-h-44 overflow-y-auto divide-y dark:divide-slate-700" data-testid={`${testidPrefix}-list`}>
        {filtered.length === 0 && <p className="text-xs text-slate-400 p-3 text-center">Sin usuarios.</p>}
        {filtered.map((u) => {
          const checked = selected.includes(u.email);
          return (
            <label key={u.id || u.email} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 text-sm" data-testid={`${testidPrefix}-item`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(u.email)} className="w-4 h-4 accent-[#0C314C]" />
              <span className="flex-1 min-w-0">
                <span className="font-medium text-slate-700 dark:text-slate-200">{u.nombre}</span>
                <span className="text-slate-400 text-xs ml-1 truncate">· {u.email}</span>
              </span>
            </label>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2">
        {selected.map((e) => (
          <Badge key={e} variant="secondary" className="gap-1">{e}
            <button onClick={() => onChange(selected.filter((x) => x !== e))}><X className="w-3 h-3" /></button>
          </Badge>
        ))}
        {selected.length === 0 && <span className="text-xs text-slate-400">Selecciona uno o más destinatarios de la lista.</span>}
      </div>
    </div>
  );
};

const MetricCard = ({ icon: Icon, label, value, hint, accent, testid, filterKey, active, onClick }) => {
  const clickable = !!onClick;
  return (
    <Card
      data-testid={testid}
      onClick={clickable ? onClick : undefined}
      className={`border overflow-hidden relative transition-all duration-200 ${clickable ? "cursor-pointer hover:shadow-lg hover:-translate-y-0.5" : ""} ${active ? "ring-2 ring-[#45E69A] shadow-md" : "border-slate-200 dark:border-slate-700"}`}
    >
      <div className={`absolute top-0 left-0 w-1.5 h-full ${accent}`} />
      <CardContent className="p-4 pl-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{value}</p>
            {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
          </div>
          <div className={`rounded-lg p-2 ${accent}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ChartCard = ({ title, icon: Icon, children, testid }) => (
  <Card className="border-slate-200 dark:border-slate-700 shadow-sm" data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4" style={{ color: NAVY }} />}
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{title}</p>
      </div>
      {children}
    </CardContent>
  </Card>
);

// ===== Sección SLA =====
const SLA_VISTAS = [
  { key: "por_vencer", label: "Por vencer", color: "#f59e0b", tiempoCol: "Tiempo restante", tiempoKey: "restante_horas" },
  { key: "vencidos", label: "Vencidos", color: "#ef4444", tiempoCol: "Tiempo vencido", tiempoKey: "vencido_horas" },
  { key: "en_tiempo", label: "En tiempo", color: "#22c55e", tiempoCol: "Tiempo restante", tiempoKey: "restante_horas" },
  { key: "cerrados_incumplidos", label: "Cerrados fuera de SLA", color: "#dc2626", tiempoCol: "Exceso", tiempoKey: "exceso_horas" },
];

const SLA_ESTADOS_DRILL = {
  en_tiempo: { label: "En tiempo", color: "#22c55e", tiempoCol: "Restante (h)", tiempoKey: "restante_horas" },
  por_vencer: { label: "Por vencer", color: "#f59e0b", tiempoCol: "Restante (h)", tiempoKey: "restante_horas" },
  vencidos: { label: "Vencidos", color: "#ef4444", tiempoCol: "Vencido (h)", tiempoKey: "vencido_horas" },
  cerrados_cumplidos: { label: "Cerrados en SLA", color: "#0C314C", tiempoCol: "Resolución (h)", tiempoKey: "horas_resolucion" },
  cerrados_incumplidos: { label: "Cerrados fuera de SLA", color: "#dc2626", tiempoCol: "Exceso (h)", tiempoKey: "exceso_horas" },
};

const SlaBreakdownTable = ({ title, icon: Icon, rows, testid, onCellClick, subtitle }) => (
  <Card className="border-slate-200 dark:border-slate-700 shadow-sm" data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {Icon && <Icon className="w-4 h-4" style={{ color: NAVY }} />}
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{title}</p>
        {subtitle && <span className="text-[11px] text-slate-400">{subtitle}</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-white" style={{ backgroundColor: NAVY }}>
              <th className="py-2 px-3 rounded-l">Nombre</th>
              <th className="py-2 px-2 text-center">Con SLA</th>
              <th className="py-2 px-2 text-center text-green-300">En tiempo</th>
              <th className="py-2 px-2 text-center text-amber-300">Por vencer</th>
              <th className="py-2 px-2 text-center text-red-300">Vencidos</th>
              <th className="py-2 px-2 text-center">En SLA</th>
              <th className="py-2 px-2 text-center">Fuera</th>
              <th className="py-2 px-3 text-right rounded-r">% Cumpl.</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).length === 0 && (
              <tr><td colSpan={8} className="py-5 text-center text-slate-400">Sin tickets con SLA para los filtros seleccionados</td></tr>
            )}
            {(rows || []).map((b) => {
              const clk = (estado, node, val) => (
                onCellClick && val > 0
                  ? <button type="button" onClick={() => onCellClick(b.nombre, estado)} className="hover:underline underline-offset-2 cursor-pointer" title="Ver tickets" data-testid={`${testid}-cell-${estado}`}>{node}</button>
                  : node
              );
              return (
              <tr key={b.nombre} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <td className="py-2 px-3 font-medium text-slate-700 dark:text-slate-200">{b.nombre}</td>
                <td className="py-2 px-2 text-center font-semibold">{b.total_sla}</td>
                <td className="py-2 px-2 text-center text-green-600 dark:text-green-400">{clk("en_tiempo", b.en_tiempo, b.en_tiempo)}</td>
                <td className="py-2 px-2 text-center">
                  {clk("por_vencer", b.por_vencer > 0 ? <Badge className="bg-amber-100 text-amber-700 border-0">{b.por_vencer}</Badge> : <span className="text-slate-400">0</span>, b.por_vencer)}
                </td>
                <td className="py-2 px-2 text-center">
                  {clk("vencidos", b.vencidos > 0 ? <Badge className="bg-red-100 text-red-700 border-0">{b.vencidos}</Badge> : <span className="text-slate-400">0</span>, b.vencidos)}
                </td>
                <td className="py-2 px-2 text-center" style={{ color: LIME }}>{clk("cerrados_cumplidos", b.cerrados_cumplidos, b.cerrados_cumplidos)}</td>
                <td className="py-2 px-2 text-center text-red-500">{clk("cerrados_incumplidos", b.cerrados_incumplidos, b.cerrados_incumplidos)}</td>
                <td className="py-2 px-3 text-right font-semibold" style={{ color: b.pct_cumplimiento == null ? "#94a3b8" : b.pct_cumplimiento >= 80 ? "#16a34a" : b.pct_cumplimiento >= 50 ? "#f59e0b" : "#ef4444" }}>
                  {b.pct_cumplimiento == null ? "—" : `${b.pct_cumplimiento}%`}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

export default function TicketsStatsTab({ modulo = null }) {
  const { getAuthHeaders, user } = useAuth();
  // Cabeceras con módulo (Hallazgos Auditoria) cuando aplica
  const authH = () => {
    const base = (getAuthHeaders)();
    return modulo ? { ...base, headers: { ...base.headers, "X-Modulo": modulo } } : base;
  };
  const isAdmin = ["administrador", "admin", "superadmin"].includes((user?.role || "").toLowerCase());
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState(null);
  const [ejecutivoId, setEjecutivoId] = useState("all");
  const [departamentoId, setDepartamentoId] = useState("all");
  const [subcategoria, setSubcategoria] = useState("all");
  const [prioridad, setPrioridad] = useState("all");
  const [tipoFiltro, setTipoFiltro] = useState("all");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [filtro, setFiltro] = useState("all");
  // Email report dialog
  const [emailDialog, setEmailDialog] = useState(false);
  const [emails, setEmails] = useState([]);
  const [sending, setSending] = useState(false);
  // Programación de reporte automático
  const [schedDialog, setSchedDialog] = useState(false);
  const [sched, setSched] = useState(null);
  const [savingSched, setSavingSched] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [userCatalog, setUserCatalog] = useState([]);

  // Ordenamiento de la tabla "Desempeño por ejecutivo"
  // Por defecto: Total descendente (el ejecutivo con más carga aparece primero).
  const [sortKey, setSortKey] = useState("total");
  const [sortDir, setSortDir] = useState("desc");
  // Sección SLA: lista de tickets visible
  const [slaVista, setSlaVista] = useState("por_vencer");
  // Drill-down SLA: modal con los tickets detrás de cada número
  const [slaDrill, setSlaDrill] = useState(null);
  // SLA de tickets enviados a otros departamentos (scope por rol en backend)
  const [slaEnviados, setSlaEnviados] = useState(null);
  useEffect(() => {
    const p = new URLSearchParams();
    if (desde) p.append("desde", desde);
    if (hasta) p.append("hasta", hasta);
    axios.get(`${API}/tickets/stats/sla-enviados?${p.toString()}`, getAuthHeaders())
      .then((r) => setSlaEnviados(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desde, hasta]);

  const abrirSlaDrill = (origen) => (nombre, estado) => {
    const meta = SLA_ESTADOS_DRILL[estado] || {};
    let lista = [];
    if (origen === "dept") lista = (((stats || {}).sla_detalle?.tickets || {})[estado] || []).filter((t) => t.departamento === nombre);
    else if (origen === "ejec") lista = (((stats || {}).sla_detalle?.tickets || {})[estado] || []).filter((t) => t.ejecutivo === nombre);
    else lista = (((slaEnviados || {}).tickets || {})[estado] || []).filter((t) => t.departamento === nombre);
    setSlaDrill({ titulo: `${nombre} — ${meta.label || estado} (${lista.length})`, lista, estado, conCreador: origen === "env" });
  };
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const getSortVal = (e, key) => {
    if (key === "ejecutivo") return (e.ejecutivo_nombre || "").toLowerCase();
    if (key === "total") return e.total || 0;
    if (key === "urgentes") return e.urgentes || 0;
    if (key === "cierre") return e.tiempo_promedio_cierre_horas ?? -1;
    return (e.estados || {})[key] || 0;
  };
  const sortEjecutivos = (list) => {
    const arr = [...(list || [])];
    if (!sortKey) return arr;
    arr.sort((a, b) => {
      const va = getSortVal(a, sortKey);
      const vb = getSortVal(b, sortKey);
      let cmp;
      if (typeof va === "string") cmp = va.localeCompare(vb);
      else cmp = va - vb;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  };
  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ChevronsUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  useEffect(() => {
    axios.get(`${API}/tickets/estadisticas-gestion/destinatarios`, authH())
      .then((r) => setUserCatalog(r.data?.items || []))
      .catch(() => setUserCatalog([]));
  }, [getAuthHeaders]);

  const openSched = async () => {
    setSchedDialog(true);
    try {
      const [c, h] = await Promise.all([
        axios.get(`${API}/tickets/reporte-programado/config`, authH()),
        axios.get(`${API}/tickets/reporte-programado/historial`, authH()),
      ]);
      setSched(c.data);
      setHistorial(h.data?.items || []);
    } catch (e) {
      toast.error("No se pudo cargar la configuración");
    }
  };
  const saveSched = async () => {
    setSavingSched(true);
    try {
      const res = await axios.put(`${API}/tickets/reporte-programado/config`, {
        activo: sched.activo, frecuencia: sched.frecuencia, dia_semana: sched.dia_semana,
        dia_mes: sched.dia_mes, hora_envio: sched.hora_envio, emails: sched.emails || [],
      }, authH());
      setSched(res.data);
      toast.success("Programación guardada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar la programación");
    } finally { setSavingSched(false); }
  };
  const enviarAhoraSched = async () => {
    setSendingNow(true);
    try {
      const res = await axios.post(`${API}/tickets/reporte-programado/enviar-ahora`, {}, authH());
      if (res.data?.skipped) toast.info(`No enviado: ${res.data.reason}`);
      else toast.success(`Reporte enviado a ${res.data.enviados}/${res.data.total} destinatario(s)`);
      const h = await axios.get(`${API}/tickets/reporte-programado/historial`, authH());
      setHistorial(h.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar ahora");
    } finally { setSendingNow(false); }
  };

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    if (ejecutivoId !== "all") p.append("ejecutivo_id", ejecutivoId);
    if (departamentoId !== "all") p.append("categoria_id", departamentoId);
    if (subcategoria !== "all") p.append("sub_categoria", subcategoria);
    if (prioridad !== "all") p.append("prioridad", prioridad);
    if (tipoFiltro !== "all") p.append("tipo", tipoFiltro);
    if (desde) p.append("desde", desde);
    if (hasta) p.append("hasta", hasta);
    if (filtro !== "all") p.append("filtro", filtro);
    return p;
  }, [ejecutivoId, departamentoId, subcategoria, prioridad, tipoFiltro, desde, hasta, filtro]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tickets/estadisticas-gestion?${buildParams().toString()}`, authH());
      setStats(res.data);
    } catch (e) {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, buildParams]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const limpiar = () => {
    setEjecutivoId("all"); setDepartamentoId("all"); setSubcategoria("all");
    setPrioridad("all"); setTipoFiltro("all"); setDesde(""); setHasta(""); setFiltro("all");
  };
  const toggleFiltro = (key) => setFiltro((prev) => (prev === key ? "all" : key));

  const filtrosBody = () => ({
    ejecutivo_id: ejecutivoId !== "all" ? ejecutivoId : null,
    categoria_id: departamentoId !== "all" ? departamentoId : null,
    sub_categoria: subcategoria !== "all" ? subcategoria : null,
    prioridad: prioridad !== "all" ? prioridad : null,
    tipo: tipoFiltro !== "all" ? tipoFiltro : null,
    desde: desde || null, hasta: hasta || null, filtro: filtro !== "all" ? filtro : null,
  });

  const exportar = async () => {
    setExporting(true);
    try {
      const auth = authH();
      const res = await axios.get(`${API}/tickets/estadisticas-gestion/export?${buildParams().toString()}`, { ...auth, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `estadisticas_mesa_ayuda_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte exportado (solo lo visualizado)");
    } catch (e) {
      toast.error("Error al exportar el reporte");
    } finally { setExporting(false); }
  };

  const enviarReporte = async () => {
    if (emails.length === 0) { toast.error("Agregue al menos un correo"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/tickets/estadisticas-gestion/enviar-reporte`, { emails, ...filtrosBody() }, authH());
      toast.success(`Reporte enviado a ${res.data.enviados}/${res.data.total} destinatario(s)`);
      setEmailDialog(false); setEmails([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar el reporte");
    } finally { setSending(false); }
  };

  const ejecutivosCatalog = stats?.filtro_ejecutivos || [];
  const departamentosCatalog = stats?.filtro_departamentos || [];
  const subcategoriasCatalog = stats?.filtro_subcategorias || [];
  const prioridadesCatalog = stats?.filtro_prioridades || [];
  const estados = stats?.estados_disponibles || ["abierto", "en_progreso", "pendiente", "resuelto", "cerrado", "completado"];
  const porEstadoTotal = stats?.por_estado_total || {};

  const estadoData = stats ? Object.entries(stats.por_estado || {}).map(([k, v]) => ({ name: cap(k), key: k, value: v })) : [];
  const prioData = stats ? Object.entries(stats.por_prioridad || {}).map(([k, v]) => ({ name: cap(k), key: k, value: v })) : [];
  const deptData = stats ? Object.entries(stats.por_departamento || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ name: k, value: v })) : [];
  const subData = stats ? Object.entries(stats.por_subcategoria || {}).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([k, v]) => ({ name: k, value: v })) : [];
  const slaPrioData = stats ? Object.entries(stats.sla_por_prioridad || {}).map(([k, v]) => ({ name: cap(k), key: k, pct: v.pct ?? 0 })) : [];
  const ejBarData = stats ? (stats.por_ejecutivo || []).slice(0, 8).map((e) => ({ name: e.ejecutivo_nombre, Abiertos: e.abiertos, Cerrados: e.cerrados })) : [];
  const tipoData = stats ? (stats.por_tipo || []) : [];
  const TIPO_META = {
    "Queja Cliente": { color: "#ef4444", bg: "bg-red-50 dark:bg-red-900/20", border: "border-red-200 dark:border-red-800", text: "text-red-700 dark:text-red-300" },
    "Solicitud": { color: "#0C314C", bg: "bg-blue-50 dark:bg-blue-900/20", border: "border-blue-200 dark:border-blue-800", text: "text-blue-700 dark:text-blue-300" },
    "Oportunidad de Mejora": { color: "#10b981", bg: "bg-emerald-50 dark:bg-emerald-900/20", border: "border-emerald-200 dark:border-emerald-800", text: "text-emerald-700 dark:text-emerald-300" },
  };
  const serie = stats?.serie_temporal || [];

  return (
    <div className="rounded-xl p-4 md:p-6 bg-gradient-to-b from-[#0C314C]/[0.06] via-[#45E69A]/[0.04] to-transparent dark:from-[#0C314C]/30 dark:to-transparent space-y-6" data-testid="tickets-stats-tab">
      {/* Banner corporativo + acciones */}
      <div className="relative overflow-hidden rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" style={{ background: `linear-gradient(110deg, ${NAVY} 0%, #2a4d7a 70%, ${NAVY} 100%)` }}>
        <div className="absolute right-0 top-0 h-full w-40" style={{ background: `linear-gradient(90deg, transparent, ${LIME}22)` }} />
        <div className="relative flex items-center gap-3 text-white">
          <div className="rounded-lg p-2" style={{ backgroundColor: `${LIME}33` }}>
            <BarChart3 className="w-6 h-6" style={{ color: LIME }} />
          </div>
          <div>
            <h2 className="text-lg font-bold leading-tight">Panel de Estadísticas</h2>
            <p className="text-white/70 text-xs">Indicadores de gestión de {modulo ? "Hallazgos Auditoria" : "la Mesa de Ayuda"}{filtro !== "all" ? ` · vista: ${cap(filtro)}` : ""}</p>
          </div>
        </div>
        <div className="relative flex items-center gap-2">
          {isAdmin && !modulo && (
            <>
              <Button onClick={openSched} variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="stats-schedule-btn">
                <CalendarClock className="w-4 h-4 mr-2" /> Programar
              </Button>
              <Button onClick={() => setEmailDialog(true)} variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="stats-send-report-btn">
                <Send className="w-4 h-4 mr-2" /> Enviar Reporte
              </Button>
            </>
          )}
          <Button onClick={exportar} disabled={exporting} className="text-[#0C314C] hover:opacity-90 font-semibold" style={{ backgroundColor: LIME }} data-testid="stats-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Exportar Excel
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-end bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700 shadow-sm">
        <div className="flex flex-col gap-1 min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Ejecutivo</Label>
          <Select value={ejecutivoId} onValueChange={setEjecutivoId}>
            <SelectTrigger data-testid="stats-filter-ejecutivo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los ejecutivos</SelectItem>
              {ejecutivosCatalog.map((e) => (<SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1 min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Departamento</Label>
          <Select value={departamentoId} onValueChange={setDepartamentoId}>
            <SelectTrigger data-testid="stats-filter-departamento"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los departamentos</SelectItem>
              {departamentosCatalog.map((d) => (<SelectItem key={d.id} value={d.id}>{d.nombre}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1 min-w-[170px]">
          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Subcategoría</Label>
          <Select value={subcategoria} onValueChange={setSubcategoria}>
            <SelectTrigger data-testid="stats-filter-subcategoria"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las subcategorías</SelectItem>
              {subcategoriasCatalog.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1 min-w-[140px]">
          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Prioridad</Label>
          <Select value={prioridad} onValueChange={setPrioridad}>
            <SelectTrigger data-testid="stats-filter-prioridad"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las prioridades</SelectItem>
              {prioridadesCatalog.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Desde</Label>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="border rounded px-2 py-2 text-sm bg-white dark:bg-slate-900 h-10" data-testid="stats-filter-desde" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Hasta</Label>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="border rounded px-2 py-2 text-sm bg-white dark:bg-slate-900 h-10" data-testid="stats-filter-hasta" />
        </div>
        <Button variant="outline" onClick={limpiar} data-testid="stats-clear-filters" className="h-10">
          <RotateCcw className="w-4 h-4 mr-2" /> Limpiar
        </Button>
        {filtro !== "all" && (
          <Badge className="h-7 px-3 self-center" style={{ backgroundColor: LIME, color: NAVY }} data-testid="stats-active-filter-badge">
            Filtrando: {cap(filtro)}
          </Badge>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando estadísticas...
        </div>
      ) : !stats ? (
        <p className="text-sm text-slate-400 py-10 text-center">No se pudieron cargar las estadísticas.</p>
      ) : (
        <>
          <p className="text-[11px] text-slate-400">Las tarjetas con borde resaltado son <strong>clickeables</strong>: filtran todo el panel y la exportación.</p>
          {/* Total + tarjetas por ESTADO (separadas) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard testid="stat-total" icon={FolderOpen} label="Total tickets" value={stats.total} accent="bg-[#0C314C]" active={filtro === "all"} onClick={() => setFiltro("all")} />
            {estados.map((est) => {
              const m = ESTADO_META[est] || { label: cap(est), accent: "bg-slate-500", icon: Layers };
              return (
                <MetricCard key={est} testid={`stat-${est}`} icon={m.icon} label={m.label} value={porEstadoTotal[est] || 0} accent={m.accent} active={filtro === est} onClick={() => toggleFiltro(est)} />
              );
            })}
            <MetricCard testid="stat-urgentes" icon={Zap} label="Urgentes" value={stats.urgentes} accent="bg-red-500" active={filtro === "urgentes"} onClick={() => toggleFiltro("urgentes")} />
            <MetricCard testid="stat-reabiertos" icon={RotateCcw} label="Reabiertos" value={stats.reabiertos} hint="Volvieron a abrirse" accent="bg-rose-500" active={filtro === "reabiertos"} onClick={() => toggleFiltro("reabiertos")} />
            <MetricCard testid="stat-backlog" icon={Archive} label="Backlog (+7 días)" value={stats.backlog_7d} hint="Abiertos antiguos" accent="bg-amber-500" active={filtro === "backlog"} onClick={() => toggleFiltro("backlog")} />
          </div>

          {/* Seguimiento de Detractores (solo Hallazgos Auditoria) */}
          {modulo && stats.detractores_total != null && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="detractores-tracking">
              <MetricCard testid="stat-detractores-total" icon={ThumbsDown} label="Detractores generados" value={stats.detractores_total} hint="Hallazgos por NPS bajo" accent="bg-red-600" />
              <MetricCard testid="stat-detractores-cerrados" icon={ShieldCheck} label="Detractores cerrados conforme" value={stats.detractores_cerrados_conforme} hint="Con cierre definitivo" accent="bg-emerald-600" />
            </div>
          )}

          {/* Evolución mensual de detractores por departamento */}
          {modulo && stats.detractores_evolucion && (
            <Card className="border-0 shadow-sm" data-testid="detractores-evolucion">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Evolución mensual de Detractores por departamento (últimos 6 meses)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.detractores_evolucion.series.length === 0 ? (
                  <p className="text-sm text-slate-400 py-6 text-center" data-testid="detractores-evolucion-vacio">
                    Sin detractores registrados en los últimos 6 meses.
                  </p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart
                      data={stats.detractores_evolucion.meses.map((m, i) => ({
                        mes: m,
                        ...Object.fromEntries(stats.detractores_evolucion.series.map(s => [s.departamento, s.valores[i]])),
                      }))}
                      margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {stats.detractores_evolucion.series.map((s, idx) => (
                        <Bar
                          key={s.departamento}
                          dataKey={s.departamento}
                          stackId="detr"
                          fill={["#dc2626", "#0C314C", "#0d9488", "#f59e0b", "#7c3aed", "#0ea5e9", "#84cc16", "#ec4899"][idx % 8]}
                          radius={idx === stats.detractores_evolucion.series.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          )}

          {/* Métricas de tiempo / SLA (no filtrables) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard testid="stat-tprom-cierre" icon={Timer} label="T. promedio de cierre" value={fmtHoras(stats.tiempo_promedio_cierre_horas)} hint="Creación → resolución" accent="bg-emerald-500" />
            <MetricCard testid="stat-primera-resp" icon={MessageSquareReply} label="1ª respuesta prom." value={fmtHoras(stats.tiempo_promedio_primera_respuesta_horas)} hint="Creación → 1er avance" accent="bg-cyan-500" />
            <MetricCard testid="stat-tprom-abierto" icon={Clock} label="Antigüedad prom. abiertos" value={fmtHoras(stats.tiempo_promedio_abierto_horas)} hint="Tiempo sin cerrar" accent="bg-orange-500" />
            <MetricCard testid="stat-sla" icon={Gauge} label="Cumplimiento SLA" value={stats.sla_cumplimiento_pct == null ? "—" : `${stats.sla_cumplimiento_pct}%`} hint={`${stats.sla_evaluados} evaluados`} accent="bg-violet-500" />
          </div>

          {/* Serie temporal */}
          <ChartCard title="Tickets a lo largo del tiempo" icon={TrendingUp} testid="chart-serie">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={serie} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="gCreados" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={NAVY} stopOpacity={0.5} /><stop offset="95%" stopColor={NAVY} stopOpacity={0} /></linearGradient>
                  <linearGradient id="gCerrados" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={LIME} stopOpacity={0.6} /><stop offset="95%" stopColor={LIME} stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="fecha" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="creados" name="Creados" stroke={NAVY} strokeWidth={2} fill="url(#gCreados)" />
                <Area type="monotone" dataKey="cerrados" name="Cerrados" stroke={LIME} strokeWidth={2} fill="url(#gCerrados)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <ChartCard title="Por estado" testid="chart-estado">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={estadoData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                    {estadoData.map((d) => <Cell key={d.key} fill={(ESTADO_META[d.key] || {}).color || NAVY} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Por prioridad" testid="chart-prioridad">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={prioData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="value" name="Tickets" radius={[6, 6, 0, 0]}>
                    {prioData.map((d) => <Cell key={d.key} fill={PRIO_COLOR[d.key] || "#3b82f6"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Cumplimiento SLA por prioridad (%)" testid="chart-sla-prio">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={slaPrioData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" /><YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => `${v}%`} />
                  <Bar dataKey="pct" name="% en SLA" radius={[6, 6, 0, 0]}>
                    {slaPrioData.map((d) => <Cell key={d.key} fill={d.pct >= 80 ? LIME : d.pct >= 50 ? "#f59e0b" : "#ef4444"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* ===== Sección SLA: Monitoreo del tiempo de resolución ===== */}
          {stats.sla_detalle && (() => {
            const sla = stats.sla_detalle;
            const res = sla.resumen || {};
            const vistaMeta = SLA_VISTAS.find((v) => v.key === slaVista) || SLA_VISTAS[0];
            const listado = (sla.tickets || {})[slaVista] || [];
            return (
              <Card className="border-2 shadow-sm" style={{ borderColor: NAVY }} data-testid="stats-sla-section">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ShieldCheck className="w-5 h-5" style={{ color: NAVY }} />
                    <p className="text-base font-bold text-slate-800 dark:text-slate-100">Monitoreo SLA — Tiempo de Resolución</p>
                    <span className="text-[11px] text-slate-400">Por vencer = queda ≤ 25% del SLA · Respeta los filtros aplicados</span>
                  </div>

                  {/* KPIs SLA */}
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                    <MetricCard icon={CheckCircle2} label="En tiempo" value={res.en_tiempo ?? 0} hint="Abiertos dentro del SLA" accent="bg-green-500" testid="sla-kpi-en-tiempo" />
                    <MetricCard icon={Timer} label="Por vencer" value={res.por_vencer ?? 0} hint="Queda ≤ 25% del SLA" accent="bg-amber-500" testid="sla-kpi-por-vencer" />
                    <MetricCard icon={AlertTriangle} label="Vencidos" value={res.vencidos ?? 0} hint="Abiertos fuera de plazo" accent="bg-red-500" testid="sla-kpi-vencidos" />
                    <MetricCard icon={CheckCheck} label="Cerrados en SLA" value={res.cerrados_cumplidos ?? 0} hint="Resueltos a tiempo" accent="bg-[#0C314C]" testid="sla-kpi-cerrados-ok" />
                    <MetricCard icon={X} label="Cerrados fuera" value={res.cerrados_incumplidos ?? 0} hint="Resueltos tarde" accent="bg-rose-600" testid="sla-kpi-cerrados-fuera" />
                    <MetricCard icon={Gauge} label="% Cumplimiento" value={res.pct_cumplimiento_cerrados == null ? "—" : `${res.pct_cumplimiento_cerrados}%`} hint={`${res.total_evaluados ?? 0} tickets con SLA`} accent="bg-lime-500" testid="sla-kpi-pct" />
                  </div>

                  {/* Desglose por departamento y ejecutivo */}
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                    <SlaBreakdownTable title="SLA por departamento" icon={FolderOpen} rows={sla.por_departamento} testid="sla-tabla-departamento" onCellClick={abrirSlaDrill("dept")} subtitle="Clic en un número para ver los tickets" />
                    <SlaBreakdownTable title="SLA por ejecutivo" icon={Users} rows={sla.por_ejecutivo} testid="sla-tabla-ejecutivo" onCellClick={abrirSlaDrill("ejec")} subtitle="Clic en un número para ver los tickets" />
                  </div>

                  {/* SLA de tickets enviados a OTROS departamentos (scope por rol) */}
                  {slaEnviados && (
                    <div data-testid="sla-enviados-section">
                      {(slaEnviados.por_departamento || []).length > 0 ? (
                        <SlaBreakdownTable
                        title="SLA — Tickets enviados a otros departamentos"
                        icon={FolderOpen}
                        rows={slaEnviados.por_departamento}
                        testid="sla-tabla-enviados"
                        onCellClick={abrirSlaDrill("env")}
                        subtitle={`${slaEnviados.scope === "admin" ? "Todos los tickets del sistema" : slaEnviados.scope === "supervisor" ? "Tickets creados por ti y tus supervisados" : "Tickets creados por ti"} hacia otros departamentos · Clic en un número para ver cuáles son`}
                      />
                      ) : (
                        <Card className="border-slate-200 dark:border-slate-700 shadow-sm">
                          <CardContent className="p-4">
                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">SLA — Tickets enviados a otros departamentos</p>
                            <p className="text-xs text-slate-400 mt-1" data-testid="sla-enviados-empty">Sin tickets con SLA enviados a otros departamentos{desde || hasta ? " en el período filtrado" : ""}.</p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}

                  {/* Modal drill-down: los tickets detrás de cada número */}
                  <Dialog open={!!slaDrill} onOpenChange={(o) => !o && setSlaDrill(null)}>
                    <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="sla-drill-modal">
                      <DialogHeader>
                        <DialogTitle className="text-base">{slaDrill?.titulo}</DialogTitle>
                        <DialogDescription className="text-xs">Tickets que componen este número del SLA</DialogDescription>
                      </DialogHeader>
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="w-full text-sm" data-testid="sla-drill-table">
                          <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wide text-white" style={{ backgroundColor: SLA_ESTADOS_DRILL[slaDrill?.estado]?.color || NAVY }}>
                              <th className="py-2 px-3">Ticket</th>
                              <th className="py-2 px-3">Título</th>
                              <th className="py-2 px-3">Departamento</th>
                              <th className="py-2 px-3">Ejecutivo</th>
                              {slaDrill?.conCreador && <th className="py-2 px-3">Creado por</th>}
                              <th className="py-2 px-2 text-center">Prioridad</th>
                              <th className="py-2 px-3 text-right">{SLA_ESTADOS_DRILL[slaDrill?.estado]?.tiempoCol || "Horas"}</th>
                              <th className="py-2 px-3 text-right">Fecha límite</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(slaDrill?.lista || []).length === 0 && (
                              <tr><td colSpan={8} className="py-5 text-center text-slate-400">Sin tickets</td></tr>
                            )}
                            {(slaDrill?.lista || []).map((t) => (
                              <tr key={t.numero} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                <td className="py-2 px-3 font-semibold text-slate-700 dark:text-slate-200">#{t.numero}</td>
                                <td className="py-2 px-3 max-w-[220px] truncate" title={t.titulo}>{t.titulo}</td>
                                <td className="py-2 px-3 text-slate-600 dark:text-slate-300">{t.departamento}</td>
                                <td className="py-2 px-3 text-slate-600 dark:text-slate-300">{t.ejecutivo}</td>
                                {slaDrill?.conCreador && <td className="py-2 px-3 text-slate-600 dark:text-slate-300">{t.creador}</td>}
                                <td className="py-2 px-2 text-center"><Badge variant="outline" className="capitalize">{t.prioridad}</Badge></td>
                                <td className="py-2 px-3 text-right tabular-nums font-semibold">{t[SLA_ESTADOS_DRILL[slaDrill?.estado]?.tiempoKey] ?? "—"}</td>
                                <td className="py-2 px-3 text-right text-slate-500 text-xs">{t.fecha_limite ? new Date(t.fecha_limite).toLocaleString("es-DO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Listados de tickets */}
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      {SLA_VISTAS.map((v) => {
                        const n = ((sla.tickets || {})[v.key] || []).length;
                        const activo = slaVista === v.key;
                        return (
                          <button
                            key={v.key}
                            type="button"
                            onClick={() => setSlaVista(v.key)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${activo ? "text-white shadow" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:shadow"}`}
                            style={activo ? { backgroundColor: v.color, borderColor: v.color } : { borderColor: v.color, color: v.color }}
                            data-testid={`sla-vista-${v.key}`}
                          >
                            {v.label} ({n})
                          </button>
                        );
                      })}
                    </div>
                    <div className="overflow-x-auto border rounded-lg max-h-80 overflow-y-auto">
                      <table className="w-full text-sm" data-testid="sla-tickets-table">
                        <thead className="sticky top-0">
                          <tr className="text-left text-[11px] uppercase tracking-wide text-white" style={{ backgroundColor: vistaMeta.color }}>
                            <th className="py-2 px-3">Ticket</th>
                            <th className="py-2 px-3">Título</th>
                            <th className="py-2 px-3">Departamento</th>
                            <th className="py-2 px-3">Ejecutivo</th>
                            <th className="py-2 px-2 text-center">Prioridad</th>
                            <th className="py-2 px-2 text-center">SLA</th>
                            <th className="py-2 px-3 text-right">{vistaMeta.tiempoCol}</th>
                            <th className="py-2 px-3 text-right">Fecha límite</th>
                          </tr>
                        </thead>
                        <tbody>
                          {listado.length === 0 && (
                            <tr><td colSpan={8} className="py-6 text-center text-slate-400">Sin tickets en "{vistaMeta.label}"</td></tr>
                          )}
                          {listado.map((t) => (
                            <tr key={t.numero} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                              <td className="py-2 px-3 font-semibold text-slate-700 dark:text-slate-200">#{t.numero}</td>
                              <td className="py-2 px-3 max-w-[220px] truncate text-slate-600 dark:text-slate-300" title={t.titulo}>{t.titulo || "—"}</td>
                              <td className="py-2 px-3 text-slate-600 dark:text-slate-300">{t.departamento}</td>
                              <td className="py-2 px-3 text-slate-600 dark:text-slate-300">{t.ejecutivo}</td>
                              <td className="py-2 px-2 text-center">
                                <Badge variant="outline" style={{ color: PRIO_COLOR[t.prioridad] || NAVY, borderColor: PRIO_COLOR[t.prioridad] || NAVY }}>{cap(t.prioridad)}</Badge>
                              </td>
                              <td className="py-2 px-2 text-center text-slate-500">{fmtHoras(t.sla_horas)}</td>
                              <td className="py-2 px-3 text-right font-bold" style={{ color: vistaMeta.color }}>{fmtHoras(t[vistaMeta.tiempoKey])}</td>
                              <td className="py-2 px-3 text-right text-xs text-slate-500">{(t.fecha_limite || "").slice(0, 16).replace("T", " ")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Departamento + Subcategoría (dinámicos) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ChartCard title="Por departamento a asignar" testid="chart-departamento">
              <ResponsiveContainer width="100%" height={Math.max(200, deptData.length * 38)}>
                <BarChart data={deptData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="value" name="Tickets" radius={[0, 6, 6, 0]}>
                    {deptData.map((d, i) => <Cell key={d.name} fill={DEPT_PALETTE[i % DEPT_PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Por subcategoría" icon={Layers} testid="chart-subcategoria">
              <ResponsiveContainer width="100%" height={Math.max(200, subData.length * 36)}>
                <BarChart data={subData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="value" name="Tickets" radius={[0, 6, 6, 0]}>
                    {subData.map((d, i) => <Cell key={d.name} fill={DEPT_PALETTE[(i + 2) % DEPT_PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Desglose por tipo de ticket */}
          <Card className="border-slate-200 dark:border-slate-700 shadow-sm" data-testid="stats-por-tipo">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Layers className="w-4 h-4" style={{ color: NAVY }} />
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Por tipo de ticket</p>
                <span className="text-[11px] text-slate-400">(click para filtrar el dashboard)</span>
                {tipoFiltro !== "all" && (
                  <button onClick={() => setTipoFiltro("all")} className="ml-auto text-[11px] font-medium text-[#0C314C] dark:text-blue-400 hover:underline" data-testid="stats-tipo-clear">
                    Filtro: {tipoFiltro} ✕
                  </button>
                )}
              </div>
              {tipoData.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">Sin datos para los filtros seleccionados</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {tipoData.map((t) => {
                    const meta = TIPO_META[t.tipo] || TIPO_META["Solicitud"];
                    const activo = tipoFiltro === t.tipo;
                    return (
                      <button
                        key={t.tipo}
                        type="button"
                        onClick={() => setTipoFiltro((prev) => (prev === t.tipo ? "all" : t.tipo))}
                        className={`text-left rounded-xl border p-4 transition-all hover:shadow-md ${meta.bg} ${activo ? "ring-2 ring-offset-1" : meta.border}`}
                        style={activo ? { boxShadow: `0 0 0 2px ${meta.color}` } : undefined}
                        data-testid={`stats-tipo-${t.tipo.replace(/\s+/g, "-").toLowerCase()}`}
                        title={activo ? "Click para quitar el filtro" : `Click para filtrar el dashboard por ${t.tipo}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className={`text-sm font-bold ${meta.text}`}>{t.tipo}</span>
                          <span className="text-2xl font-extrabold" style={{ color: meta.color }}>{t.total}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center mb-3">
                          <div>
                            <p className="text-base font-bold text-slate-700 dark:text-slate-200">{t.abiertos}</p>
                            <p className="text-[10px] uppercase tracking-wide text-slate-400">Abiertos</p>
                          </div>
                          <div>
                            <p className="text-base font-bold text-slate-700 dark:text-slate-200">{t.cerrados}</p>
                            <p className="text-[10px] uppercase tracking-wide text-slate-400">Cerrados</p>
                          </div>
                          <div>
                            <p className="text-base font-bold text-red-500">{t.urgentes}</p>
                            <p className="text-[10px] uppercase tracking-wide text-slate-400">Urgentes</p>
                          </div>
                        </div>
                        <div className="border-t pt-2 space-y-1" style={{ borderColor: meta.color + "33" }}>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500 dark:text-slate-400">T. prom. resolución</span>
                            <span className="font-semibold text-slate-700 dark:text-slate-200">{fmtHoras(t.tiempo_promedio_cierre_horas)}</span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Carga por ejecutivo */}
          <ChartCard title="Carga por ejecutivo (top 8)" icon={Users} testid="chart-ejecutivos">
            <ResponsiveContainer width="100%" height={Math.max(180, ejBarData.length * 42)}>
              <BarChart data={ejBarData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Abiertos" stackId="a" fill="#3b82f6" />
                <Bar dataKey="Cerrados" stackId="a" fill={LIME} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Tabla por ejecutivo con TODOS los estados */}
          <Card className="border-slate-200 dark:border-slate-700 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4" style={{ color: NAVY }} />
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Desempeño por ejecutivo</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="stats-ejecutivos-table">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-white select-none" style={{ backgroundColor: NAVY }}>
                      <th className="py-2 px-3 rounded-l cursor-pointer hover:bg-white/10 transition-colors" onClick={() => toggleSort("ejecutivo")} data-testid="stats-ej-sort-ejecutivo">
                        <span className="inline-flex items-center gap-1">Ejecutivo <SortIcon col="ejecutivo" /></span>
                      </th>
                      <th className="py-2 px-2 text-center cursor-pointer hover:bg-white/10 transition-colors" onClick={() => toggleSort("total")} data-testid="stats-ej-sort-total">
                        <span className="inline-flex items-center justify-center gap-1">Total <SortIcon col="total" /></span>
                      </th>
                      {estados.map((e) => (
                        <th key={e} className="py-2 px-2 text-center cursor-pointer hover:bg-white/10 transition-colors" onClick={() => toggleSort(e)} data-testid={`stats-ej-sort-${e}`}>
                          <span className="inline-flex items-center justify-center gap-1">{(ESTADO_META[e] || {}).label || cap(e)} <SortIcon col={e} /></span>
                        </th>
                      ))}
                      <th className="py-2 px-2 text-center cursor-pointer hover:bg-white/10 transition-colors" onClick={() => toggleSort("urgentes")} data-testid="stats-ej-sort-urgentes">
                        <span className="inline-flex items-center justify-center gap-1">Urg. <SortIcon col="urgentes" /></span>
                      </th>
                      <th className="py-2 px-3 text-right rounded-r cursor-pointer hover:bg-white/10 transition-colors" onClick={() => toggleSort("cierre")} data-testid="stats-ej-sort-cierre">
                        <span className="inline-flex items-center justify-end gap-1">T. cierre <SortIcon col="cierre" /></span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats.por_ejecutivo || []).length === 0 && (
                      <tr><td colSpan={estados.length + 4} className="py-6 text-center text-slate-400">Sin datos para los filtros seleccionados</td></tr>
                    )}
                    {sortEjecutivos(stats.por_ejecutivo || []).map((e) => (
                      <tr key={e.ejecutivo_id || e.ejecutivo_nombre} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="py-2 px-3 font-medium text-slate-700 dark:text-slate-200">{e.ejecutivo_nombre}</td>
                        <td className="py-2 px-2 text-center font-semibold">{e.total}</td>
                        {estados.map((st) => (
                          <td key={st} className="py-2 px-2 text-center" style={{ color: (ESTADO_META[st] || {}).color }}>{(e.estados || {})[st] || 0}</td>
                        ))}
                        <td className="py-2 px-2 text-center">
                          {e.urgentes > 0 ? <Badge className="bg-red-100 text-red-700 border-0">{e.urgentes}</Badge> : <span className="text-slate-400">0</span>}
                        </td>
                        <td className="py-2 px-3 text-right text-slate-600 dark:text-slate-300">{fmtHoras(e.tiempo_promedio_cierre_horas)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Dialog Enviar Reporte */}
      <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
        <DialogContent className="sm:max-w-md" data-testid="stats-send-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" style={{ color: NAVY }} /> Enviar Reporte por Correo</DialogTitle>
            <DialogDescription>Se enviará un Excel con <strong>solo lo que estás visualizando</strong> (filtros actuales) + un resumen en el cuerpo del correo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <RecipientPicker catalog={userCatalog} selected={emails} onChange={setEmails} testidPrefix="stats-report-recipients" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog(false)}>Cancelar</Button>
            <Button onClick={enviarReporte} disabled={sending || emails.length === 0} style={{ backgroundColor: NAVY }} className="text-white" data-testid="stats-report-send-confirm">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Programar reporte automático */}
      <Dialog open={schedDialog} onOpenChange={setSchedDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="stats-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarClock className="w-5 h-5" style={{ color: NAVY }} /> Reporte Automático</DialogTitle>
            <DialogDescription>Programa el envío recurrente del reporte de Mesa de Ayuda (datos globales del período) a una lista fija de destinatarios.</DialogDescription>
          </DialogHeader>
          {!sched ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="text-sm font-semibold">Envío automático</p>
                  <p className="text-xs text-slate-500">Activa o pausa la programación</p>
                </div>
                <Switch checked={!!sched.activo} onCheckedChange={(v) => setSched({ ...sched, activo: v })} data-testid="sched-activo-switch" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Frecuencia</Label>
                  <Select value={sched.frecuencia} onValueChange={(v) => setSched({ ...sched, frecuencia: v })}>
                    <SelectTrigger data-testid="sched-frecuencia"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="semanal">Semanal</SelectItem>
                      <SelectItem value="mensual">Mensual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {sched.frecuencia === "mensual" ? (
                  <div className="space-y-1">
                    <Label className="text-xs">Día del mes (1-28)</Label>
                    <Input type="number" min={1} max={28} value={sched.dia_mes} onChange={(e) => setSched({ ...sched, dia_mes: parseInt(e.target.value || "1") })} data-testid="sched-dia-mes" />
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Label className="text-xs">Día de la semana</Label>
                    <Select value={String(sched.dia_semana)} onValueChange={(v) => setSched({ ...sched, dia_semana: parseInt(v) })}>
                      <SelectTrigger data-testid="sched-dia-semana"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"].map((d, i) => (
                          <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs">Hora de envío (RD)</Label>
                  <Input type="time" value={sched.hora_envio} onChange={(e) => setSched({ ...sched, hora_envio: e.target.value })} data-testid="sched-hora" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Destinatarios</Label>
                <RecipientPicker
                  catalog={userCatalog}
                  selected={sched.emails || []}
                  onChange={(arr) => setSched({ ...sched, emails: arr })}
                  testidPrefix="sched-recipients"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={saveSched} disabled={savingSched} style={{ backgroundColor: NAVY }} className="text-white" data-testid="sched-save-btn">
                  {savingSched ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Guardar
                </Button>
                <Button onClick={enviarAhoraSched} disabled={sendingNow} variant="outline" data-testid="sched-send-now-btn">
                  {sendingNow ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Enviar ahora
                </Button>
              </div>
              {historial.length > 0 && (
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1"><History className="w-3.5 h-3.5" /> Últimos envíos</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="sched-historial">
                    {historial.map((h) => (
                      <div key={h.id} className="flex justify-between items-center text-xs bg-slate-50 dark:bg-slate-800 rounded px-2 py-1">
                        <span>{new Date(h.fecha).toLocaleString()} · {h.periodo}</span>
                        <Badge className={h.enviados > 0 ? "bg-green-100 text-green-700 border-0" : "bg-red-100 text-red-700 border-0"}>{h.enviados}/{h.total}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedDialog(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
